import type { List } from '@gonfalon/immutable-shim/immutableShim';
import { is, Set } from '@gonfalon/immutable-shim/immutableShim';
import { coerceToType } from '@gonfalon/types/coerceToType';
import { isJSONValue } from '@gonfalon/types/isJSONValue';
import { isBoolean, isString } from 'lodash';

import { nilFilter } from '#utils/collectionUtils';
import { VariationType, variationTypes as vt } from '#utils/flags/flagConstants';
import type { Flag } from '#utils/flags/records/Flag';
import { couldBeNumerical } from '#utils/flags/validation';
import { areAllBooleans, areAllJSON, areAllNumbers, couldAllBeJSON } from '#utils/typeUtils';

export function detectVariationType(flag: Flag) {
  const vs = flag.variations
    .filter((v) => !v.isEmpty())
    .map((v) => v.value)
    .toArray();

  if (!vs.length) {
    return vt.STRING;
  }

  if (areAllBooleans(vs)) {
    return vt.BOOLEAN;
  }

  if (areAllNumbers(vs)) {
    return vt.NUMBER;
  }

  if (areAllJSON(vs)) {
    return vt.JSON;
  }

  return vt.STRING;
}

export function inferVariationType(flag: Flag, isClone?: boolean) {
  const vs: List<object | string> = flag.variations
    .filter((v) => !v.isEmpty())
    .map((v) => {
      // if the value is an immutable Record or other object, or a clone of an existing flag, return it (because toString will return undesirable results)
      if (v.value instanceof Object || isClone) {
        return v.value;
      }
      // otherwise try to reset everything to strings so the following checks work properly
      return v.value.toString ? v.value.toString() : v.value;
    });

  const booleans = Set.of(true, false);

  if (vs.isEmpty()) {
    return vt.STRING;
  }

  const asBooleans: Set<boolean> = vs
    .map((v) => {
      switch (v) {
        case 'true':
          return true;
        case 'false':
          return false;
        default:
          return undefined;
      }
    })
    .filter(nilFilter)
    .toSet();

  if (is(vs.toSet(), booleans) || (is(asBooleans.toSet(), booleans) && !isClone)) {
    return vt.BOOLEAN;
  }

  if (vs.every(couldBeNumerical)) {
    return vt.NUMBER;
  }

  if (couldAllBeJSON(vs)) {
    return vt.JSON;
  }

  return vt.STRING;
}

export function hasHomogeneousVariationTypes(flag: Flag) {
  const vs = flag.variations.filter((v) => !v.isEmpty()).map((v) => v.value);
  return vs.every(isString) || vs.every(couldBeNumerical) || vs.every(isJSONValue) || vs.every(isBoolean);
}

const scientificNotation = /^-?\d+(\.\d+)?e-?\d+$/;
export function forceVariationType(flag: Flag, t: VariationType) {
  return flag.update('variations', (vs) =>
    vs.map((v) => {
      if (!v.isEmpty()) {
        if (t === VariationType.NUMBER) {
          const asString = coerceToType(v.value, 'number').toString();
          if (asString !== v.value && !scientificNotation.test(asString)) {
            // don't prevent the ability to enter leading or trailing zeroes for number variations
            return v;
          }
        }
        return v.update('value', (value) => coerceToType(value, t));
      }
      return v;
    }),
  );
}
